// ...existing code...
import React, { useEffect, useRef, useState, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import ReactSlider from "react-slider";
import { Button } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PropertyList from "./PropertyListCarolineMap";
import Header from "./Header";

mapboxgl.accessToken =
  "pk.eyJ1IjoiYWJhaWppdGgxMjc5IiwiYSI6ImNtNGNqdzE4NDA2MGoyaXFwdXh6cjVqMWQifQ.OpRKi-Zt3NIOjXFz-hd-DA";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return debouncedValue;
};

// Helpers
const formatCurrency = (num) => {
  const val = parseFloat(num);
  return !isNaN(val) ? `$${val.toLocaleString("en-US")}` : "N/A";
};
const formatArea = (num) => {
  const val = parseFloat(num);
  return !isNaN(val) ? val.toFixed(3) + " ac" : "N/A";
};
const formatNumber = (num, decimals = 0) => {
  const val = parseFloat(num);
  return !isNaN(val) ? val.toFixed(decimals) : "N/A";
};
const formatPercent = (num) => {
  const val = parseFloat(num);
  return !isNaN(val) ? val.toFixed(2) + "%" : "N/A";
};

// bbox helper
const getFeatureBBox = (feature) => {
  if (!feature || !feature.geometry) return null;
  const coords = [];
  const collect = (c) => {
    if (!c) return;
    if (typeof c[0] === "number") coords.push(c);
    else c.forEach(collect);
  };
  collect(feature.geometry.coordinates);
  if (coords.length === 0) return null;
  const lons = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  return [Math.min(...lons), Math.min(...lats), Math.max(...lons), Math.max(...lats)];
};

// MAIN COMPONENT
const ParcelScreenCarolinaMapView = () => {
  // refs
  const initialFetchDone = useRef(false);
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const geocoderContainerRef = useRef(null);

  // data
  const [parcelData, setParcelData] = useState([]);
  const [filteredParcels, setFilteredParcels] = useState([]);
  const [mbtileFeatures, setMbtileFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // view
  const [showMap, setShowMap] = useState(true); // show NC boundary initially
  const [mapInitialized, setMapInitialized] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(localStorage.getItem("isSubscribed") === "true");

  // filters / ui state
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [areaRange, setAreaRange] = useState([0, 500000]);
  const [zestimateRange, setZestimateRange] = useState([0, 2000000]);
  const [spreadRange, setSpreadRange] = useState([-100, 1000]);
  const [maxUnitRange, setMaxUnitRange] = useState([0, 100]);
  const [zoneCodeFilter, setZoneCodeFilter] = useState("All");
  const [countyFilter, setCountyFilter] = useState("wake"); // API filter default
  const [activeCounty, setActiveCounty] = useState(null); // county selected on map
  const [isVacant, setIsVacant] = useState(true);
  const [listedSaleFilter, setListedSaleFilter] = useState("All");
  const [showOnlyListed, setShowOnlyListed] = useState(false);

  const [sortBy, setSortBy] = useState("none");
  const [sortDirection, setSortDirection] = useState("desc");

  // slider limits (populated from API once)
  const [sliderMinPrice, setSliderMinPrice] = useState(0);
  const [sliderMaxPrice, setSliderMaxPrice] = useState(1000000);
  const [sliderMinArea, setSliderMinArea] = useState(0);
  const [sliderMaxArea, setSliderMaxArea] = useState(500000);
  const [sliderMinZestimate, setSliderMinZestimate] = useState(0);
  const [sliderMaxZestimate, setSliderMaxZestimate] = useState(2000000);
  const [sliderMinSpread, setSliderMinSpread] = useState(-100);
  const [sliderMaxSpread, setSliderMaxSpread] = useState(1000);
  const [sliderMinMaxUnit, setSliderMinMaxUnit] = useState(0);
  const [sliderMaxMaxUnit, setSliderMaxMaxUnit] = useState(100);

  // debounced values for API calls / mbtiles filter expressions
  const debouncedPriceRange = useDebounce(priceRange, 800);
  const debouncedAreaRange = useDebounce(areaRange, 800);
  const debouncedZestimateRange = useDebounce(zestimateRange, 800);
  const debouncedSpreadRange = useDebounce(spreadRange, 800);
  const debouncedMaxUnitRange = useDebounce(maxUnitRange, 800);

  // MBTILES config — set REACT_APP_TILE_URL and REACT_APP_TILE_LAYER in .env
  const TILE_URL_TEMPLATE = process.env.REACT_APP_TILE_URL || "/tiles/{county}/{z}/{x}/{y}.pbf";
  const MBTILE_VECTOR_LAYER = process.env.REACT_APP_TILE_LAYER || "parcels";
  const MBTILE_ZOOM_THRESHOLD = parseInt(process.env.REACT_APP_MBTILE_ZOOM || "16", 10);

  const [mbtilesMode, setMbtilesMode] = useState(false);

  const countyOptions = ["wake", "chatham"];
  const listedSaleOptions = ["ActiveListing", "PendingSale"];

  // Build JS filter predicate for properties (used for API list and for optional client-side filtering)
  const applyFiltersToProperties = (props = {}) => {
    const landValue = parseFloat(props.landvalueperunit) || 0;
    const area = parseFloat(props.arealotsf) || 0;
    const zestimate = parseFloat(props.zestimate) || 0;
    const spread = parseFloat(props.spreadpercentage) || 0;
    const maxUnit = parseFloat(props.noallowableunits || props.totalunits) || 0;
    const zone = (props.zonedcodelocal || "Unknown").toString();

    const matchLandValue = sliderMaxPrice === 0 || landValue === 0 || (landValue >= priceRange[0] && landValue <= priceRange[1]);
    const matchArea = sliderMaxArea === 0 || area === 0 || (area >= areaRange[0] && area <= areaRange[1]);
    const matchZestimate = sliderMaxZestimate === 0 || zestimate === 0 || (zestimate >= zestimateRange[0] && zestimate <= zestimateRange[1]);
    const matchSpread = sliderMaxSpread === 0 || spread === 0 || (spread >= spreadRange[0] && spread <= spreadRange[1]);
    const matchMaxUnit = sliderMaxMaxUnit === 0 || maxUnit === 0 || (maxUnit >= maxUnitRange[0] && maxUnit <= maxUnitRange[1]);
    const matchZone = zoneCodeFilter === "All" || String(zone).toLowerCase() === String(zoneCodeFilter).toLowerCase();
    const matchListed = !showOnlyListed || listedSaleFilter === "All" || (props.listedsale && props.listedsale === listedSaleFilter);

    return matchLandValue && matchArea && matchZestimate && matchSpread && matchMaxUnit && matchZone && matchListed;
  };

  // Build Mapbox expression to apply as setFilter on mbtiles layers
  const buildMbtilesFilterExpression = () => {
    const expr = ["all"];

    if (activeCounty) {
      // assume tile has 'county' property — downcase comparison
      expr.push(["==", ["downcase", ["get", "county"]], activeCounty.toLowerCase()]);
    }

    if (showOnlyListed) {
      if (listedSaleFilter === "All") {
        expr.push(["any", ["==", ["get", "listedsale"], "ActiveListing"], ["==", ["get", "listedsale"], "PendingSale"]]);
      } else {
        expr.push(["==", ["get", "listedsale"], listedSaleFilter]);
      }
    }

    const addRangeCond = (field, min, max) => {
      expr.push([
        "any",
        ["==", ["get", field], 0],
        ["all", [">=", ["to-number", ["get", field]], min], ["<=", ["to-number", ["get", field]], max]],
      ]);
    };

    addRangeCond("landvalueperunit", debouncedPriceRange[0], debouncedPriceRange[1]);
    addRangeCond("arealotsf", debouncedAreaRange[0], debouncedAreaRange[1]);
    addRangeCond("zestimate", debouncedZestimateRange[0], debouncedZestimateRange[1]);
    addRangeCond("spreadpercentage", debouncedSpreadRange[0], debouncedSpreadRange[1]);
    addRangeCond("noallowableunits", debouncedMaxUnitRange[0], debouncedMaxUnitRange[1]);

    if (zoneCodeFilter && zoneCodeFilter !== "All") {
      expr.push(["==", ["get", "zonedcodelocal"], zoneCodeFilter]);
    }

    return expr;
  };

  // Fetch from API using current UI filters and sorting — used to populate left list when not in mbtilesMode
  const fetchParcelData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append("is_vaccant", isVacant ? "1" : "0");
      if (countyFilter && countyFilter !== "All") params.append("county", countyFilter);
      if (zoneCodeFilter && zoneCodeFilter !== "All") params.append("zonedcodelocal", zoneCodeFilter);

      if (isVacant && showOnlyListed) {
        params.append("listedsale", listedSaleFilter || "All");
      }

      // ranges — append when user changed from slider defaults
      if (sliderMaxPrice > 0 && (priceRange[0] !== sliderMinPrice || priceRange[1] !== sliderMaxPrice)) {
        if (priceRange[0] > sliderMinPrice) params.append("totallandvalue_min", Math.round(priceRange[0]));
        if (priceRange[1] < sliderMaxPrice) params.append("totallandvalue_max", Math.round(priceRange[1]));
      }
      if (sliderMaxArea > 0 && (areaRange[0] !== sliderMinArea || areaRange[1] !== sliderMaxArea)) {
        if (areaRange[0] > sliderMinArea) params.append("parcelsize_min", areaRange[0].toFixed(2));
        if (areaRange[1] < sliderMaxArea) params.append("parcelsize_max", areaRange[1].toFixed(2));
      }
      if (sliderMaxZestimate > 0 && (zestimateRange[0] !== sliderMinZestimate || zestimateRange[1] !== sliderMaxZestimate)) {
        if (zestimateRange[0] > sliderMinZestimate) params.append("zestimate_min", Math.round(zestimateRange[0]));
        if (zestimateRange[1] < sliderMaxZestimate) params.append("zestimate_max", Math.round(zestimateRange[1]));
      }

      if (sliderMaxMaxUnit > 0 && (maxUnitRange[0] !== sliderMinMaxUnit || maxUnitRange[1] !== sliderMaxMaxUnit)) {
        if (maxUnitRange[0] > sliderMinMaxUnit) params.append("noallowableunits_min", Math.round(maxUnitRange[0]));
        if (maxUnitRange[1] < sliderMaxMaxUnit) params.append("noallowableunits_max", Math.round(maxUnitRange[1]));
      }

      // sorting to backend
      if (sortBy && sortBy !== "none") params.append("sort_by", sortBy);
      if (sortDirection) params.append("sort_order", sortDirection);

      const url = `https://beta.flowlogic.com/api/parcels/list-parcel-data/?${params.toString()}`;
      console.log("API URL:", url);

      const token = localStorage.getItem("authToken");
      const resp = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const features = data.features || [];
      console.log("Loaded", features.length, "parcels (API)");
      setParcelData(features);
      setFilteredParcels(features);

      if (!initialFetchDone.current) {
        updateSliderRanges(features);
        initialFetchDone.current = true;
      }
    } catch (err) {
      console.error("Error fetching parcel data:", err);
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  // update slider defaults from API dataset (only once)
  const updateSliderRanges = (features) => {
    if (!features || features.length === 0) return;
    const landValues = features.map((f) => parseFloat(f.properties?.landvalueperunit) || 0).filter((v) => v > 0);
    if (landValues.length > 0) {
      const min = Math.min(...landValues);
      const max = Math.max(...landValues);
      setSliderMinPrice(min);
      setSliderMaxPrice(max);
      setPriceRange([min, max]);
    }
    const areaValues = features.map((f) => parseFloat(f.properties?.arealotsf) || 0).filter((v) => v > 0);
    if (areaValues.length > 0) {
      const min = Math.min(...areaValues);
      const max = Math.max(...areaValues);
      setSliderMinArea(min);
      setSliderMaxArea(max);
      setAreaRange([min, max]);
    }
    const zVals = features.map((f) => parseFloat(f.properties?.zestimate) || 0).filter((v) => v > 0);
    if (zVals.length > 0) {
      const min = Math.min(...zVals);
      const max = Math.max(...zVals);
      setSliderMinZestimate(min);
      setSliderMaxZestimate(max);
      setZestimateRange([min, max]);
    }
    const spreadVals = features.map((f) => parseFloat(f.properties?.spreadpercentage) || 0);
    if (spreadVals.length > 0) {
      const min = Math.min(...spreadVals);
      const max = Math.max(...spreadVals);
      setSliderMinSpread(min);
      setSliderMaxSpread(max);
      setSpreadRange([min, max]);
    }
    const maxUnitVals = features.map((f) => parseFloat(f.properties?.noallowableunits || 0)).filter((v) => v > 0);
    if (maxUnitVals.length > 0) {
      const min = Math.floor(Math.min(...maxUnitVals));
      const max = Math.ceil(Math.max(...maxUnitVals));
      setSliderMinMaxUnit(min);
      setSliderMaxMaxUnit(max);
      setMaxUnitRange([min, max]);
    }
  };

  // update API geojson source
  const updateMapData = (parcels) => {
    if (!mapRef.current || !mapRef.current.getSource) return;
    try {
      if (mapRef.current.getSource("parcel-source")) {
        mapRef.current.getSource("parcel-source").setData({ type: "FeatureCollection", features: parcels });
        fitMapToParcels(parcels);
      }
    } catch (e) {
      console.error("Error updating map:", e);
    }
  };

  // fit bounds to parcels
  const fitMapToParcels = (parcels) => {
    if (!mapRef.current || !parcels || parcels.length === 0) return;
    try {
      const coords = parcels
        .map((f) => {
          const lon = parseFloat(f.properties?.longitude);
          const lat = parseFloat(f.properties?.latitude);
          if (isNaN(lon) || isNaN(lat)) return null;
          return [lon, lat];
        })
        .filter(Boolean);
      if (coords.length === 0) return;
      const lons = coords.map((c) => c[0]);
      const lats = coords.map((c) => c[1]);
      mapRef.current.fitBounds([[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]], {
        padding: 50,
        duration: 1200,
        maxZoom: 14,
      });
    } catch (err) {
      console.error("Error fitting bounds:", err);
    }
  };

  // clicking a list row
  const handleRowClick = (property, index) => {
    setSelectedRow(index);
    if (!showMap || !mapRef.current) return;
    const lat = parseFloat(property.latitude || property.properties?.latitude);
    const lng = parseFloat(property.longitude || property.properties?.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    mapRef.current.flyTo({ center: [lng, lat], zoom: 17, duration: 1200 });
    new mapboxgl.Popup()
      .setLngLat([lng, lat])
      .setHTML(`<div style="padding:10px">${property.addrline1 || property.properties?.addrline1 || "N/A"}</div>`)
      .addTo(mapRef.current);
  };

  const clearAllFilters = () => {
    setCountyFilter("wake");
    setIsVacant(true);
    setShowOnlyListed(false);
    setListedSaleFilter("All");
    setPriceRange([sliderMinPrice, sliderMaxPrice]);
    setAreaRange([sliderMinArea, sliderMaxArea]);
    setZestimateRange([sliderMinZestimate, sliderMaxZestimate]);
    setSpreadRange([sliderMinSpread, sliderMaxSpread]);
    if (sliderMaxMaxUnit > 0) setMaxUnitRange([sliderMinMaxUnit, sliderMaxMaxUnit]);
    setZoneCodeFilter("All");
    setSortBy("none");
  };

  // fetch API when filters change and not in mbtiles mode
  useEffect(() => {
    if (mbtilesMode) return;
    fetchParcelData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countyFilter,
    isVacant,
    showOnlyListed,
    listedSaleFilter,
    zoneCodeFilter,
    debouncedMaxUnitRange,
    debouncedPriceRange,
    debouncedAreaRange,
    debouncedZestimateRange,
    debouncedSpreadRange,
    sortBy,
    sortDirection,
  ]);

  // client-side apply filters to API results; update map source when not in mbtilesMode
  useEffect(() => {
    const filtered = parcelData.filter((f) => applyFiltersToProperties(f.properties || {}));
    setFilteredParcels(filtered);
    if (mapRef.current && mapInitialized && !mbtilesMode) updateMapData(filtered);
  }, [parcelData, priceRange, areaRange, zestimateRange, spreadRange, maxUnitRange, zoneCodeFilter, showOnlyListed, listedSaleFilter, mapInitialized, mbtilesMode]);

  // sortedParcels (API-based)
  const sortedParcels = useMemo(() => {
    if (sortBy === "none") return filteredParcels;
    const s = [...filteredParcels].sort((a, b) => {
      const aVal = parseFloat(a.properties?.[sortBy]) || 0;
      const bVal = parseFloat(b.properties?.[sortBy]) || 0;
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
    return s;
  }, [filteredParcels, sortBy, sortDirection]);

  // zoneCodes from API
  const zoneCodes = useMemo(() => {
    const codes = new Set();
    parcelData.forEach((f) => {
      const z = f.properties?.zonedcodelocal || "Unknown";
      if (z) codes.add(String(z));
    });
    return ["All", ...Array.from(codes).sort()];
  }, [parcelData]);

  // MAP init: show NC boundary initially; add API source and mbtiles placeholder
  useEffect(() => {
    if (!showMap) return;
    if (mapInitialized && mapRef.current) return;

    const initMap = async () => {
      try {
        mapRef.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: "mapbox://styles/mapbox/light-v11",
          center: [-79.806419, 35.630066],
          zoom: 6,
        });

        mapRef.current.addControl(new mapboxgl.NavigationControl(), "bottom-right");

        mapRef.current.on("load", async () => {
          // add NC counties GeoJSON (place under public/assets/nc_counties.geojson)
          if (!mapRef.current.getSource("nc-counties")) {
            try {
              mapRef.current.addSource("nc-counties", { type: "geojson", data: "/assets/nc_counties.geojson" });
              mapRef.current.addLayer({ id: "nc-counties-fill", type: "fill", source: "nc-counties", paint: { "fill-color": "#1565C0", "fill-opacity": 0.15 } });
              mapRef.current.addLayer({ id: "nc-counties-outline", type: "line", source: "nc-counties", paint: { "line-color": "#0D47A1", "line-width": 1.2 } });

              // county click -> focus + set activeCounty and ensure mbtiles source configured
              mapRef.current.on("click", "nc-counties-fill", (e) => {
                const feat = e.features && e.features[0];
                if (!feat) return;
                const props = feat.properties || {};
                const countyName = (props.County || props.county || props.NAME || props.name || "").toString().toLowerCase();
                setActiveCounty(countyName);
                setCountyFilter(countyName);
                const bbox = getFeatureBBox(feat);
                if (bbox) mapRef.current.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 40, duration: 800, maxZoom: 12 });
                addOrUpdateMbtilesSource(countyName);
              });

              mapRef.current.on("mouseenter", "nc-counties-fill", () => (mapRef.current.getCanvas().style.cursor = "pointer"));
              mapRef.current.on("mouseleave", "nc-counties-fill", () => (mapRef.current.getCanvas().style.cursor = ""));
            } catch (err) {
              console.error("Error adding nc-counties source/layers:", err);
            }
          }

          // add API geojson source used for list-mode visualization
          if (!mapRef.current.getSource("parcel-source")) {
            mapRef.current.addSource("parcel-source", { type: "geojson", data: { type: "FeatureCollection", features: sortedParcels } });

            mapRef.current.addLayer({
              id: "parcel-fill",
              type: "fill",
              source: "parcel-source",
              paint: {
                "fill-color": ["match", ["get", "parcelcustomfemaflood"], "X", "#4CAF50", "AE", "#FF9800", "VE", "#E91E63", "A", "#2196F3", "#9fa2e7"],
                "fill-opacity": 0.45,
              },
            });

            mapRef.current.addLayer({
              id: "parcel-outline",
              type: "line",
              source: "parcel-source",
              paint: { "line-color": "#333", "line-width": 0.6, "line-opacity": 0.6 },
            });

            mapRef.current.on("click", "parcel-fill", (e) => {
              const props = e.features[0].properties || {};
              new mapboxgl.Popup().setLngLat(e.lngLat).setHTML(`<div style="padding:10px">${props.addrline1 || "N/A"}</div>`).addTo(mapRef.current);
            });
          }

          setMapInitialized(true);
        });

        // switch to/from mbtiles when zoom crosses threshold
        mapRef.current.on("zoomend", () => {
          const zoom = mapRef.current.getZoom();
          const shouldMbtiles = zoom >= MBTILE_ZOOM_THRESHOLD && !!activeCounty;
          if (shouldMbtiles !== mbtilesMode) {
            setMbtilesMode(shouldMbtiles);
            if (shouldMbtiles) {
              addOrUpdateMbtilesSource(activeCounty);
              setTimeout(() => updateListFromMbtiles(), 300);
            } else {
              setMbtileFeatures([]);
              if (mapRef.current.getSource("parcel-source")) updateMapData(sortedParcels);
            }
          } else if (shouldMbtiles) {
            updateListFromMbtiles();
          }
        });

        // update mbtiles list on pan/idle
        mapRef.current.on("moveend", () => {
          if (mbtilesMode) updateListFromMbtiles();
        });
      } catch (err) {
        console.error("Error initializing map:", err);
      }
    };

    initMap();
    setTimeout(() => mapRef.current && mapRef.current.resize(), 200);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        setMapInitialized(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap]);

  // Add or update mbtiles vector source / layers for selected county
  const addOrUpdateMbtilesSource = (countyName) => {
    if (!mapRef.current || !countyName) return;
    const tileUrl = TILE_URL_TEMPLATE.replace("{county}", countyName);
    try {
      // remove existing mbtiles layers & source if present
      if (mapRef.current.getLayer("mbtiles-fill")) {
        try {
          mapRef.current.removeLayer("mbtiles-fill");
          mapRef.current.removeLayer("mbtiles-outline");
        } catch (e) {}
      }
      if (mapRef.current.getSource("mbtiles-source")) {
        try {
          mapRef.current.removeSource("mbtiles-source");
        } catch (e) {}
      }
    } catch (e) {}
    try {
      mapRef.current.addSource("mbtiles-source", { type: "vector", tiles: [tileUrl], maxzoom: 20 });
      mapRef.current.addLayer({
        id: "mbtiles-fill",
        type: "fill",
        source: "mbtiles-source",
        "source-layer": MBTILE_VECTOR_LAYER,
        paint: {
          "fill-color": ["match", ["get", "parcelcustomfemaflood"], "X", "#4CAF50", "AE", "#FF9800", "VE", "#E91E63", "A", "#2196F3", "#9fa2e7"],
          "fill-opacity": 0.6,
        },
      });
      mapRef.current.addLayer({
        id: "mbtiles-outline",
        type: "line",
        source: "mbtiles-source",
        "source-layer": MBTILE_VECTOR_LAYER,
        paint: { "line-color": "#222", "line-width": 0.6 },
      });

      // apply current UI filters to vector layers
      applyFiltersToMbtilesLayers();
    } catch (err) {
      console.error("Error adding mbtiles source/layers:", err);
    }
  };

  // set filter expression on mbtiles layers
  const applyFiltersToMbtilesLayers = () => {
    if (!mapRef.current) return;
    const expr = buildMbtilesFilterExpression();
    try {
      if (mapRef.current.getLayer("mbtiles-fill")) mapRef.current.setFilter("mbtiles-fill", expr);
      if (mapRef.current.getLayer("mbtiles-outline")) mapRef.current.setFilter("mbtiles-outline", expr);
      if (mbtilesMode) setTimeout(() => updateListFromMbtiles(), 200);
    } catch (err) {
      console.error("Error applying filter to mbtiles layers:", err);
    }
  };

  // query rendered features from mbtiles layer and produce list
  const updateListFromMbtiles = () => {
    if (!mapRef.current || !mapRef.current.getLayer("mbtiles-fill")) return;
    try {
      const features = mapRef.current.queryRenderedFeatures({ layers: ["mbtiles-fill"] }) || [];
      const list = features.map((f) => ({ id: f.id || f.properties?.geo_id || Math.random().toString(36).slice(2, 9), properties: f.properties || {}, geometry: f.geometry }));
      const filtered = list.filter((feat) => applyFiltersToProperties(feat.properties || {}));
      const sorted = sortBy === "none" ? filtered : filtered.sort((a, b) => {
        const aVal = parseFloat(a.properties?.[sortBy]) || 0;
        const bVal = parseFloat(b.properties?.[sortBy]) || 0;
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      });
      setMbtileFeatures(sorted);
    } catch (err) {
      console.error("Error querying mbtiles features:", err);
    }
  };

  // when mbtiles mode / active county changes
  useEffect(() => {
    if (!mapRef.current) return;
    if (mbtilesMode) {
      addOrUpdateMbtilesSource(activeCounty);
      applyFiltersToMbtilesLayers();
      setTimeout(() => updateListFromMbtiles(), 300);
    } else {
      setMbtileFeatures([]);
      if (mapRef.current.getSource("parcel-source")) updateMapData(sortedParcels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mbtilesMode, activeCounty]);

  // when filters change while in mbtilesMode, update mbtiles layer filter expression & list
  useEffect(() => {
    if (mbtilesMode) applyFiltersToMbtilesLayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPriceRange, debouncedAreaRange, debouncedZestimateRange, debouncedSpreadRange, debouncedMaxUnitRange, zoneCodeFilter, showOnlyListed, listedSaleFilter, sortBy, sortDirection]);

  // when API-sorted data changes and map not in mbtiles mode, update api geojson source
  useEffect(() => {
    if (!mapRef.current || !mapInitialized) return;
    if (!mbtilesMode && mapRef.current.getSource("parcel-source")) updateMapData(sortedParcels);
  }, [sortedParcels, mbtilesMode, mapInitialized]);

  // which list to show
  const displayList = mbtilesMode ? mbtileFeatures : sortedParcels;

  // RENDER
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Header
        activeTab={"parcels"}
        handleTabChange={() => {}}
        toggleMenu={() => setIsMenuOpen((s) => !s)}
        isMenuOpen={isMenuOpen}
        handleLogout={() => {
          localStorage.removeItem("authToken");
          window.location.href = "/";
        }}
        geocoderContainerRef={geocoderContainerRef}
        showSearch={true}
        showFavorites={false}
        showLinks={true}
        isSubscribed={isSubscribed}
      />

      {/* KEEP existing filter-bar UI from your file (ensure controls update the state used above). */}
      {/* ...existing filter bar JSX remains unchanged... */}

      <div className="mainOuter" style={{ display: "flex", flex: 1, height: "100vh", overflow: "hidden" }}>
        <div
          className="listOuter"
          style={{
            width: showMap ? "50%" : "100%",
            height: "100%",
            borderRight: showMap ? "1px solid #ddd" : "none",
            background: "#fafafa",
            transition: "width 0.3s ease",
            overflow: "auto",
          }}
        >
          {loading ? (
            <div style={{ padding: "20px", textAlign: "center" }}>Loading...</div>
          ) : error ? (
            <div style={{ padding: "20px", textAlign: "center", color: "red" }}>Error: {error}</div>
          ) : (
            <PropertyList parcels={displayList} onRowClick={handleRowClick} />
          )}
        </div>

        {showMap && (
          <div style={{ width: "50%", height: "100%", position: "relative", transition: "width 0.3s ease" }}>
            <div ref={mapContainer} style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }} />

            {/* legend */}
            <div
              style={{
                position: "absolute",
                bottom: "10px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(255,255,255,0.95)",
                padding: "8px 12px",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                fontSize: "11px",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ width: 16, height: 16, backgroundColor: "#4CAF50", opacity: 0.5, borderRadius: 3, border: "2px solid #4CAF50" }} />
                <span>X - Minimal Risk</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ width: 16, height: 16, backgroundColor: "#FF9800", opacity: 0.5, borderRadius: 3, border: "2px solid #FF9800" }} />
                <span>AE - High Risk</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParcelScreenCarolinaMapView;
// ...existing code...